import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { ProductShape } from 'lib/util/productShape';

import baseStyles from '../base.module.css';
import styles from './ProductDetailsView.module.css';
import { CancellationPolicyTimeline } from 'components/ProductContentsPane/CancellationPolicyTimeline/CancellationPolicyTimeline';
import { LinkableTextArea } from 'components/LinkableTextArea/LinkableTextArea';
import { ContentSection } from './ContentSection/ContentSection';

type Props = {
  product: ProductShape;
};

export const ProductInfoView = ({ product }: Props) => {
  const { t } = useTranslation();

  const itemLists: Array<{
    header: string;
    items: string[];
  }> = [
    {
      header: t("What's Included"),
      items: product.inclusions,
    },
    {
      header: t("What's Not Included"),
      items: product.exclusions,
    },
    {
      header: t('Requirements'),
      items: product.requirements,
    },
    {
      header: t('Restrictions'),
      items: product.restrictions,
    },
    {
      header: t('What to Bring'),
      items: product.whatToBring,
    },
    {
      header: t('Additional Info'),
      items: product.otherInfo,
    },
    ...(product.minimumParticipants
      ? [
          {
            header: t('Minimum Participants'),
            items: [`${product.minimumParticipants}`],
          },
        ]
      : []),
  ].filter((i) => i.items.length > 0);

  return (
    <ContentSection
      title={t('Plan Information')}
      englishTitle="Plan Information"
      className={styles['information']}
    >
      <div className={styles['information__content']}>
        <ul className={styles['information__content__list']}>
          {itemLists.map((itemList, idx) => (
            <li key={idx} className={styles['information__content__list__item']}>
              <div
                className={clsx(
                  styles['information__content__list__item__ttl'],
                  baseStyles['color-bg--black'],
                  baseStyles['color-txt--white']
                )}
              >
                {itemList.header}
              </div>
              <ul className={styles['information__content__list__item__body']}>
                {itemList.items.map((item) => (
                  <li key={item}>
                    <LinkableTextArea>{item}</LinkableTextArea>
                  </li>
                ))}
              </ul>
            </li>
          ))}
          <li className={styles['information__content__list__item']}>
            <p
              className={clsx(
                styles['information__content__list__item__ttl'],
                baseStyles['color-bg--black'],
                baseStyles['color-txt--white']
              )}
            >
              {t('Cancellation Policies')}
            </p>
            <CancellationPolicyTimeline
              cancellationPolicies={product.cancellationPolicies}
              currency={product.currency}
            />
          </li>
        </ul>
      </div>
    </ContentSection>
  );
};
